<template>
  <!-- Shared status badge + dropdown for a key result. Used by the compact
       KeyResultRow and the KR detail dialog so the manual-dot affordance and
       the "Auto (from progress)" option stay identical everywhere.
       NOTE: happily-dropdown-popper goes on el-dropdown-menu directly —
       popper-class on el-dropdown silently fails for dropdown menus. -->
  <el-dropdown
    trigger="click"
    :disabled="readonly"
    class="kr-status-dropdown"
    @command="$emit('change', $event)"
  >
    <span
      class="kr-status-badge"
      :style="{
        backgroundColor: statusColor + '26',
        color: statusColor
      }"
    >
      {{ statusLabel }}
      <!-- "Set manually" affordance (contract C3): a human pinned this
           status, so value updates won't move it. Tooltip names them. -->
      <el-tooltip
        v-if="isManualStatus"
        :content="manualStatusTooltip"
        placement="top"
        :open-delay="200"
      >
        <span class="kr-status-manual-dot" />
      </el-tooltip>
      <i v-if="!readonly" class="el-icon-arrow-down" style="font-size: 10px; margin-left: 2px;" />
    </span>
    <el-dropdown-menu slot="dropdown" class="happily-dropdown-popper">
      <el-dropdown-item command="on_track">{{ $t("performance.goals.kr_status_on_track") }}</el-dropdown-item>
      <el-dropdown-item command="at_risk">{{ $t("performance.goals.kr_status_at_risk") }}</el-dropdown-item>
      <el-dropdown-item command="off_track">{{ $t("performance.goals.kr_status_off_track") }}</el-dropdown-item>
      <el-dropdown-item command="completed">{{ $t("performance.goals.kr_status_completed") }}</el-dropdown-item>
      <el-dropdown-item command="auto" divided>{{ $t("performance.goals.kr_status_auto") }}</el-dropdown-item>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<script>
import { KR_STATUS_COLORS, KR_STATUS_LABELS } from '@/constants/krStatus';
import { isAutomationActor, latestManualStatusEntry } from '@/store/utils/krUpdateRules';

export default {
  name: 'KrStatusBadge',
  props: {
    keyResult: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
  },
  computed: {
    statusColor() {
      return KR_STATUS_COLORS[this.keyResult.status] || '#909399';
    },
    statusLabel() {
      return KR_STATUS_LABELS[this.keyResult.status] || '';
    },
    isManualStatus() {
      return this.keyResult.status_source === 'manual';
    },
    manualStatusTooltip() {
      // Attribute the pinned status to the latest history entry that carries a
      // status. Automation never counts as a person (contract C5).
      const entry = latestManualStatusEntry(this.keyResult.progress_history);
      if (entry && entry.updated_by && !isAutomationActor(entry.updated_by)) {
        const actor = this.$store.getters['goals/resolveOwner'](entry.updated_by);
        const name = actor && actor.fullname ? actor.fullname : entry.updated_by.split('@')[0];
        return this.$t('performance.goals.kr_status_manual_dot_tooltip', { name });
      }
      return this.$t('performance.goals.kr_status_manual_dot_tooltip_unknown');
    },
  },
};
</script>

<style scoped>
.kr-status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* Dot on the status badge when a human pinned the status (contract C3).
   Inherits the badge color via currentColor so it works on every status. */
.kr-status-manual-dot {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.55;
  margin-left: 4px;
  flex-shrink: 0;
}
</style>
